import { memo, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Group } from 'react-konva';

import SolarPanel from '../../Panel/SolarPanel';
import UserDefinedTemporaryPanels from 'components/tool/Panel/UserDefinedTemporaryPanels';

import { getExistingPanelsGroupID, KonvaGroupNames } from '../../utils';
import type { Group as PanelGroupType } from 'konva/lib/Group';
import TempRectGroup from 'components/tool/Panel/TempRectGroup';
import { selectIsEditingModeEnabled } from 'store/selectors/toolSelector';

type Props = {
	roofSegment: RasterRoofSegment;
}

function PanelsGroup({ roofSegment }: Props): JSX.Element {

	// pick only the fields used here; whole-slice selectors re-rendered every panel group on any action
	const { invalidAzimuthFacets, selectedRoofIndex } = useSelector((state: RootState) => {
		const tool = state.toolNewPostions.data;
		return { invalidAzimuthFacets: tool.invalidAzimuthFacets, selectedRoofIndex: tool.selectedRoofIndex };
	}, shallowEqual);
	const { deletedPanels, activePanelMode, tempPanelsGroup, selectedMovablePanels } = useSelector((state: RootState) => {
		const panelData = state.panelSlice.data;
		return {
			deletedPanels: panelData.deletedPanels,
			activePanelMode: panelData.activePanelMode,
			tempPanelsGroup: panelData.tempPanelsGroup,
			selectedMovablePanels: panelData.selectedMovablePanels,
		};
	}, shallowEqual);

	const deletedPanelIds = new Set(deletedPanels.map(p => p.panelId));
	const groupID = getExistingPanelsGroupID(roofSegment.id);
	const panelGroupRef = useRef<PanelGroupType>(null);
	const isAnyEditingModeEnabled = useSelector(selectIsEditingModeEnabled);

	const canDragPanelGrp = (!isAnyEditingModeEnabled && selectedRoofIndex === roofSegment.id);

	useEffect(() => {
		panelGroupRef.current?.moveToTop();
	},[activePanelMode]);

	return (
		<Group
			id={groupID}
			listening={true}
			name={KonvaGroupNames.existingPanelsGroup}
			ref={panelGroupRef}
			draggable={canDragPanelGrp}
		>
			{
				roofSegment.panels.map((p) => {
					if (deletedPanelIds.has(p.id) || selectedMovablePanels.find(panelData => panelData.id === p.id)) return null;
					return (
						<SolarPanel
							key={p.id}
							panelData={{ ...p, userMappedFacet: !!(p.userMapped || roofSegment.userMapped) }}
							panelId={p.id}
							invalidAzimuth={invalidAzimuthFacets[roofSegment.id]}
						/>
					);
				})
			}
			{/* unsaved panels added by user */}
			<UserDefinedTemporaryPanels roofSegmentId={roofSegment.id} />
			{tempPanelsGroup.length > 0 && <TempRectGroup roofSegmentId={roofSegment.id} />}
		</Group>
	);
}
export default memo(PanelsGroup);